import { useEffect, useState } from 'react'

import useAlarmCodes from '@/hooks/useAlarmCodes'
import useApi from '@/hooks/useApi'
import type { AlarmCode } from '@/types/dashboard-types'
import { DATA_SOURCE_VALUES, type DataSource } from '@/types/data-source'
import logger from '@/utils/logger'

const log = logger({ category: 'useAlarmCodesForSource' })

let timescaleCodes: AlarmCode[] | undefined
let inFlight: Promise<AlarmCode[]> | undefined

const loadTimescaleCodes = async (api: ReturnType<typeof useApi>['api']): Promise<AlarmCode[]> => {
  if (timescaleCodes) return timescaleCodes
  inFlight ??= (async () => {
    const response = await api.get<AlarmCode[]>({
      path: `/fridge-alarm-codes?skip=0&take=5000&dataSource=${DATA_SOURCE_VALUES.timescale}`,
      origin: 'useAlarmCodesForSource',
      expiresIn: 'session',
    })
    if (response.error) {
      log.error('could not load Timescale alarm codes: %o', response)
      return []
    }
    return (response.data ?? []).sort((a, b) => a.description.trim().localeCompare(b.description.trim()))
  })()
  timescaleCodes = await inFlight
  inFlight = undefined
  return timescaleCodes
}

/**
 * The alarm codes belonging to a given back end.
 *
 * Timescale's alarm_type table is not populated yet, so selecting Timescale currently yields
 * an empty list — deliberately, rather than falling back to Fresh codes, which would pair
 * Fresh code ids with Timescale alarm data. Unlike assets and companies, alarm_type carries
 * no legacy id, so the two back ends' code ids do not correspond.
 */
const useAlarmCodesForSource = (source: DataSource): AlarmCode[] => {
  const freshCodes = useAlarmCodes()
  const { api } = useApi()
  const [loaded, setLoaded] = useState<AlarmCode[] | undefined>(timescaleCodes)

  const wantsTimescale = source === DATA_SOURCE_VALUES.timescale

  useEffect(() => {
    if (!wantsTimescale || !api || loaded) return
    let cancelled = false
    void loadTimescaleCodes(api).then((result) => {
      if (!cancelled) setLoaded(result)
    })
    return () => {
      cancelled = true
    }
  }, [wantsTimescale, api, loaded])

  return wantsTimescale ? (loaded ?? []) : freshCodes
}

export default useAlarmCodesForSource
